import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Pencil,
  Trash2,
  Tag,
  Rocket,
  CheckCircle2,
  Settings2,
  Search,
  Clock,
  User,
  ScrollText,
} from "lucide-react";
import { PageHeader } from "@/components/ui/page-header";
import { GlassCard } from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  getAllPppcActivity,
  type PppcActivityAction,
  type PppcActivityEntry,
} from "@/services/pppcActivityLog";
import {
  getAllWorkspaceActivity,
  type WorkspaceActivityAction,
  type WorkspaceActivityEntry,
} from "@/services/workspaceActivityLog";
import {
  formatInTimezone,
  formatRelativeTime,
} from "@/lib/timezonePreference";
import { useTimezonePreference } from "@/hooks/useTimezonePreference";
import { TimezoneSelect } from "@/components/system/TimezoneSelect";

/**
 * Unified activity log surface.
 *
 * Until the smplify backend exposes a first-class audit API, the console
 * persists per-feature event streams in localStorage (see
 * `pppcActivityLog` and `workspaceActivityLog`). This page aggregates
 * every available source into one searchable, filterable feed so admins
 * can answer "who changed what, when, against which app/device?" without
 * hopping between detail pages.
 *
 * Filters: source (PPPC / Workspace), action, app (bundleId or appId),
 * device id, actor, and a free-text search across summary/identifiers.
 * Live-updates via the `pppc-activity-updated` and
 * `workspace-activity-updated` window events plus cross-tab `storage`.
 */

type Source = "pppc" | "workspace";

interface UnifiedEntry {
  id: string;
  source: Source;
  /** Normalized action string ("created", "updated", "deployed", ...). */
  action: string;
  /** ISO timestamp. */
  at: string;
  actor: string | null;
  summary: string;
  /** Cross-entity pointers — only present for PPPC entries. */
  appId?: string;
  bundleId?: string;
  deviceId?: string;
  policyId?: string;
  workspaceId?: string;
  tagIds?: string[];
}

const ACTION_META: Record<
  string,
  { label: string; icon: typeof Plus; tone: string }
> = {
  created: { label: "Created", icon: Plus, tone: "bg-success-muted text-success" },
  updated: { label: "Updated", icon: Pencil, tone: "bg-primary/10 text-primary" },
  deleted: { label: "Deleted", icon: Trash2, tone: "bg-destructive/10 text-destructive" },
  tagged: { label: "Tags changed", icon: Tag, tone: "bg-accent text-accent-foreground" },
  deployed: { label: "Deployed", icon: Rocket, tone: "bg-primary/10 text-primary" },
  installed: {
    label: "Installed on device",
    icon: CheckCircle2,
    tone: "bg-success-muted text-success",
  },
};

const SOURCE_META: Record<Source, { label: string; tone: string }> = {
  pppc: { label: "PPPC", tone: "bg-primary/10 text-primary" },
  workspace: { label: "Workspace", tone: "bg-accent text-accent-foreground" },
};

// Timestamp helpers are sourced from `@/lib/timezonePreference` so the
// LogsPage, the per-policy activity log, and the embedded timeline all
// honor the same admin-selected zone (with live updates via
// `useTimezonePreference`).

function pppcSummary(e: PppcActivityEntry): string {
  if (e.summary) return e.summary;
  const meta = ACTION_META[e.action]?.label ?? e.action;
  if (e.action === "tagged") {
    const n = (e.tagIds ?? []).length;
    return `${meta} — bound to ${n} tag${n === 1 ? "" : "s"}`;
  }
  if (e.snapshot?.appName) return `${meta} — ${e.snapshot.appName}`;
  return meta;
}

function unify(
  pppc: PppcActivityEntry[],
  workspace: WorkspaceActivityEntry[],
): UnifiedEntry[] {
  const out: UnifiedEntry[] = [];
  for (const e of pppc) {
    out.push({
      id: `pppc:${e.id}`,
      source: "pppc",
      action: e.action,
      at: e.at,
      actor: e.actor,
      summary: pppcSummary(e),
      appId: e.appId,
      bundleId: e.bundleId,
      deviceId: e.deviceId,
      policyId: e.policyId,
      tagIds: e.tagIds,
    });
  }
  for (const e of workspace) {
    out.push({
      id: `ws:${e.id}`,
      source: "workspace",
      action: e.action,
      at: e.at,
      actor: e.actor,
      summary: e.summary,
      workspaceId: e.workspaceId,
    });
  }
  return out.sort((a, b) => (a.at < b.at ? 1 : -1));
}

function readAll(): UnifiedEntry[] {
  return unify(getAllPppcActivity(), getAllWorkspaceActivity());
}

export default function LogsPage() {
  const tz = useTimezonePreference();
  const [entries, setEntries] = useState<UnifiedEntry[]>(() => readAll());
  const [source, setSource] = useState<"all" | Source>("all");
  const [action, setAction] = useState<string>("all");
  const [app, setApp] = useState("");
  const [device, setDevice] = useState("");
  const [actor, setActor] = useState("");
  const [search, setSearch] = useState("");

  // Live updates: refresh whenever any audit log dispatches an event,
  // and on cross-tab storage writes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => setEntries(readAll());
    window.addEventListener("pppc-activity-updated", refresh);
    window.addEventListener("workspace-activity-updated", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("pppc-activity-updated", refresh);
      window.removeEventListener("workspace-activity-updated", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Build the filter dropdown option lists from the live data so admins
  // only see actions/actors that actually exist in the stream.
  const actionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const e of entries) set.add(e.action);
    return Array.from(set).sort();
  }, [entries]);

  const filtered = useMemo(() => {
    const appNeedle = app.trim().toLowerCase();
    const deviceNeedle = device.trim().toLowerCase();
    const actorNeedle = actor.trim().toLowerCase();
    const searchNeedle = search.trim().toLowerCase();

    return entries.filter((e) => {
      if (source !== "all" && e.source !== source) return false;
      if (action !== "all" && e.action !== action) return false;
      if (appNeedle) {
        const hay = `${e.appId ?? ""} ${e.bundleId ?? ""}`.toLowerCase();
        if (!hay.includes(appNeedle)) return false;
      }
      if (deviceNeedle) {
        if (!(e.deviceId ?? "").toLowerCase().includes(deviceNeedle)) return false;
      }
      if (actorNeedle) {
        if (!(e.actor ?? "").toLowerCase().includes(actorNeedle)) return false;
      }
      if (searchNeedle) {
        const hay = [
          e.summary,
          e.appId,
          e.bundleId,
          e.deviceId,
          e.policyId,
          e.workspaceId,
          e.actor,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(searchNeedle)) return false;
      }
      return true;
    });
  }, [entries, source, action, app, device, actor, search]);

  const hasAnyFilter =
    source !== "all" ||
    action !== "all" ||
    app !== "" ||
    device !== "" ||
    actor !== "" ||
    search !== "";

  function resetFilters() {
    setSource("all");
    setAction("all");
    setApp("");
    setDevice("");
    setActor("");
    setSearch("");
  }

  return (
    <div className="px-6 py-6 space-y-6">
      <PageHeader
        title="Activity logs"
        description="Local audit (browser session). Captures PPPC policy lifecycle and workspace configuration changes made from this browser. A backend audit feed isn't available yet."
      >
        <TimezoneSelect />
      </PageHeader>

      <div className="rounded-md border border-warning/30 bg-warning-muted/40 px-3 py-2 text-xs text-warning-foreground">
        These events are stored locally in your browser and won't appear on other devices or for other admins.
      </div>

      <GlassCard variant="default" hover={false} className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2 relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search summary, ids, actor…"
              className="pl-9"
              aria-label="Search activity logs"
            />
          </div>

          <Select value={source} onValueChange={(v) => setSource(v as typeof source)}>
            <SelectTrigger aria-label="Filter by source">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="pppc">PPPC</SelectItem>
              <SelectItem value="workspace">Workspace</SelectItem>
            </SelectContent>
          </Select>

          <Select value={action} onValueChange={setAction}>
            <SelectTrigger aria-label="Filter by action">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actionOptions.map((a) => (
                <SelectItem key={a} value={a}>
                  {ACTION_META[a]?.label ?? a}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            value={app}
            onChange={(e) => setApp(e.target.value)}
            placeholder="App / bundle id"
            aria-label="Filter by app id or bundle id"
          />
          <Input
            value={device}
            onChange={(e) => setDevice(e.target.value)}
            placeholder="Device id"
            aria-label="Filter by device id"
          />
          <Input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder="Actor email"
            aria-label="Filter by actor"
          />
        </div>

        <div className="mt-3 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing <span className="font-medium text-foreground">{filtered.length}</span> of {entries.length} event
            {entries.length === 1 ? "" : "s"}
          </p>
          {hasAnyFilter && (
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Clear filters
            </Button>
          )}
        </div>
      </GlassCard>

      <GlassCard variant="default" hover={false} className="p-0 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ScrollText className="h-12 w-12 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-sm">
              {entries.length === 0
                ? "No activity recorded yet."
                : "No events match the current filters."}
            </p>
          </div>
        ) : (
          <ScrollArea className="h-[640px]">
            <ul className="divide-y divide-border">
              {filtered.map((e) => {
                const meta = ACTION_META[e.action] ?? {
                  label: e.action,
                  icon: Settings2,
                  tone: "bg-muted text-muted-foreground",
                };
                const Icon = meta.icon;
                const sourceMeta = SOURCE_META[e.source];
                return (
                  <li key={e.id} className="flex items-start gap-3 p-4">
                    <span
                      className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${meta.tone}`}
                      aria-hidden="true"
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge className={`text-xs ${sourceMeta.tone}`} variant="outline">
                          {sourceMeta.label}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {meta.label}
                        </Badge>
                        <span
                          className="text-xs text-muted-foreground inline-flex items-center gap-1"
                          title={formatInTimezone(e.at, tz)}
                        >
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(e.at)}
                        </span>
                        {e.actor && (
                          <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {e.actor}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-sm text-foreground break-words">
                        {e.summary}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        {e.bundleId && <span>bundle: {e.bundleId}</span>}
                        {e.appId && <span>app: {e.appId}</span>}
                        {e.deviceId && <span>device: {e.deviceId}</span>}
                        {e.policyId && <span>policy: {e.policyId}</span>}
                        {e.workspaceId && <span>workspace: {e.workspaceId}</span>}
                        {e.tagIds && e.tagIds.length > 0 && (
                          <span>
                            tags: {e.tagIds.length}
                          </span>
                        )}
                        <span className="ml-auto">{formatInTimezone(e.at, tz)}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </GlassCard>
    </div>
  );
}
